'use client';
import { useEffect } from 'react';
import { useIntl } from 'react-intl';
import styles from './LiveEventPanelInfo.module.scss';
import { LoadingButton } from '@eventbrite/marmalade';
import {
  getPanelDisplayPrice,
  shouldShowDiscountSignal,
  shouldShowCheckoutButton,
  shouldShowSoldOutText,
} from '../utils';
import {
  getDisplayTimezone,
  getSingleDateDisplayString,
  isPastMidnightSingleNightEvent,
  isSameDayInTimezone,
  getSimpleFormattedDateForTwoDays,
  bulletSeparator,
} from '@/src/listings/utils/dateTime';
import { FormattedMessage } from 'react-intl';
import { LoadingSkeleton } from '@eventbrite/legacy-infra.loading-skeleton';
import { useLiveEventData } from '../hooks/useLiveEventData';
import { CheckoutButton } from '../CheckoutButton/CheckoutButton';
import { MULTIPLE_DATES, DONATION_TEXT } from '@/src/listings/components/_shared/constants';
import { useAttendeeTimezone } from '@/src/listings/hooks/useAttendeeTimezone';
import { ExploreSimilarEventsButton } from '../ExploreSimilarEventsButton';
import { CONVERSION_BAR_MESSAGES } from '../constants';
import { HighLevelStatus } from '@/src/listings/utils/highLevelStatus';
import { useDisplayStatus } from '@/src/listings/components/_shared/contexts/DisplayStatusContext';
import { useBasicInfo } from '@/src/listings/components/_shared/contexts/BasicInfoContext';
import { useIsPreview } from '@/src/listings/components/_shared/contexts/PreviewContext';
import { QUERY_PARAMS } from '@/src/listings/common/constants';
import { getWindowObject, HAS_WINDOW } from '@eventbrite/feature-detection';

const DetailsLoadingSkeleton: React.FC = () => {
  return (
    <div className={styles.detailsLoadingSkeleton}>
      <LoadingSkeleton height={15} width={150} />
      <LoadingSkeleton height={15} width={150} />
    </div>
  );
};

const ContentArea: React.FC = () => {
  const { loading } = useLiveEventData();

  if (loading) {
    return <DetailsLoadingSkeleton />;
  }
  return <SalesInformation />;
};

const SalesInformation: React.FC = () => {
  const { formatMessage, locale } = useIntl();
  const { ticketInfo, startDate, isSeries, isOnline, hideStartDate } = useLiveEventData();
  const { endDate, hideEndDate } = useBasicInfo();

  const attendeeTimezone = useAttendeeTimezone(isOnline ?? false);
  const { displayStatus } = useDisplayStatus();

  if (!startDate?.utc) {
    return null;
  }

  const showEndTimeOnly = hideStartDate && !hideEndDate && endDate?.utc;
  const timezone = getDisplayTimezone({
    isOnlineEvent: isOnline ?? false,
    eventTimezone: startDate.timezone,
    attendeeTimezone,
  });
  const isSameDay = endDate?.utc ? isSameDayInTimezone(startDate, endDate, timezone) : false;
  const isSingleNightEvent = !isSeries && isPastMidnightSingleNightEvent(startDate, endDate);
  const dateString = isSeries
    ? formatMessage(MULTIPLE_DATES)
    : !isSameDay && !isSingleNightEvent && endDate?.utc
      ? getSimpleFormattedDateForTwoDays(startDate, endDate, locale, timezone)
      : getSingleDateDisplayString(
          startDate,
          !hideStartDate,
          locale,
          timezone,
          formatMessage,
          false,
          !showEndTimeOnly ? bulletSeparator : undefined,
          true,
          isOnline ?? false,
        );

  let displayText: string | undefined;
  if (ticketInfo) {
    if (ticketInfo.hasOnlyDonationTicketsAvailable) {
      displayText = formatMessage(DONATION_TEXT);
    } else if (shouldShowSoldOutText(displayStatus, ticketInfo)) {
      displayText = CONVERSION_BAR_MESSAGES[HighLevelStatus.SOLD_OUT]
        ? formatMessage(CONVERSION_BAR_MESSAGES[HighLevelStatus.SOLD_OUT])
        : '';
    } else {
      displayText = getPanelDisplayPrice(ticketInfo, locale, formatMessage);
    }
  }

  return (
    <>
      {displayText !== undefined && (
        <div data-testid="conversion-bar-headline" className={styles.headline}>
          <span>{displayText}</span>
        </div>
      )}
      <div data-testid="conversion-bar-date" className={styles.date}>
        {dateString}
      </div>
    </>
  );
};

export const LiveEventPanelInfo: React.FC<{
  showDiscountSignal?: (show: boolean) => void;
  // Early Access window is open: lift only the not-yet-on-sale CTA suppression; all other gates apply.
  isEarlyAccessWindowOpen?: boolean;
}> = ({ showDiscountSignal = () => void {}, isEarlyAccessWindowOpen = false }) => {
  const { formatMessage } = useIntl();
  const { status } = useBasicInfo();
  const isPreview = useIsPreview();
  const { ticketInfo, loading } = useLiveEventData();
  useEffect(() => {
    if (shouldShowDiscountSignal(ticketInfo) && !ticketInfo?.hasOnlyDonationTicketsAvailable) {
      showDiscountSignal(true);
    }
  }, [ticketInfo, showDiscountSignal, ticketInfo?.hasOnlyDonationTicketsAvailable]);

  const { displayStatus } = useDisplayStatus();
  const waitlistSearchParams = HAS_WINDOW ? new URLSearchParams(getWindowObject('location')?.search ?? '') : null;
  const hasReleasedWaitlistClaim = Boolean(waitlistSearchParams?.get(QUERY_PARAMS.WAITLIST_TOKEN)?.trim().length);
  const showCheckoutButton =
    isPreview ||
    shouldShowCheckoutButton(status, displayStatus, ticketInfo, hasReleasedWaitlistClaim, isEarlyAccessWindowOpen);

  return (
    <div
      className={styles.container}
      role="region"
      aria-label={formatMessage({ id: 'Tickets price and event schedule' })}
    >
      <div className={styles.content}>
        <ContentArea />
      </div>
      <div data-testid="cb-cta-area" className={styles.ctaButtonWrapper}>
        {loading ? (
          <LoadingButton
            isLoading={loading}
            id="conversion-bar-checkout-button"
            data-testid="conversion-bar-checkout-button"
            role="button"
            variant="primary"
            className={styles.button}
          >
            <FormattedMessage id="Get tickets" defaultMessage="Get tickets" description="Checkout button CTA" />
          </LoadingButton>
        ) : showCheckoutButton ? (
          <CheckoutButton isRemindMe={false} />
        ) : (
          <ExploreSimilarEventsButton />
        )}
      </div>
    </div>
  );
};
